import { t } from '@lingui/macro';
import {
  TApplication,
  TIntroducerApplication,
} from 'lendingApi/applications.types';

/**
 * APPLICATIONS
 */
export const APPLICATION_STATUS: Record<
  TIntroducerApplication['status'],
  TIntroducerApplication['status']
> = {
  funded: 'funded',
  expired: 'expired',
  talking_to_customer: 'talking_to_customer',
  offered: 'offered',
  declined: 'declined',
  deferred: 'deferred',
  in_underwriting: 'in_underwriting',
  awaiting_underwriter: 'awaiting_underwriter',
  open: 'open',
  action_required: 'action_required',
  offer_revoked: 'offer_revoked',
  customer_to_link_open_banking: 'customer_to_link_open_banking',
  conditional_offer: 'conditional_offer',
  offer_rejected: 'offer_rejected',
  conditional_offer_criteria_met: 'conditional_offer_criteria_met',
  manual_review: 'manual_review',
} as const;

/**
 * PRODUCTS
 */
export const PRODUCT_NAMES = () => ({
  business_loan: 'Term Loan',
  term_loan: 'Term Loan',
  credit_facility: t`Flexi-Loan`,
  cbils: 'CBILS',
  iwocapay: 'iwocaPay',
  flexi24: t`Flexi-24`,
  recovery_loan: 'Recovery Loan',
  merchant_cash_advance: 'Revenue Based Loan',
});

export const PRODUCT_DISPLAY_ORDER = [
  PRODUCT_NAMES().credit_facility,
  PRODUCT_NAMES().flexi24,
  PRODUCT_NAMES().term_loan,
];

/**
 * APPLICATION TABLE
 */
export const APPLICATION_TABLE_KEYS = {
  created: 'created',
  customer_name: 'customer_name',
  status: 'status',
  product_type: 'product_type',
  term_length: 'term_length',
  expiry: 'expiry',
  company_name: 'company_name',
  approval_amount: 'approval_amount',
  requested_amount: 'requested_amount',
  referred_by: 'referred_by',
} as const;

export const APPLICATION_TABLE_KEY_NAMES = {
  created: 'Created',
  customer_name: 'Applicant',
  status: 'Application status',
  product_type: 'Product',
  term_length: 'Term length',
  expiry: 'Expiry date',
  company_name: 'Company name',
  approval_amount: 'Approval amount',
  requested_amount: 'Requested amount',
  referred_by: 'Referred by',
};

export const PRODUCT_TYPE_FILTERS = (includeTermLoan: boolean) => {
  const filters = [
    { value: '-', label: '-' },
    { value: PRODUCT_NAMES().credit_facility, label: t`Flexi-Loan` },
    { value: PRODUCT_NAMES().flexi24, label: t`Flexi-24` },
  ];

  if (includeTermLoan) {
    filters.push({
      value: PRODUCT_NAMES().term_loan,
      label: PRODUCT_NAMES().term_loan,
    });
  }
  return filters;
};

export const TERM_LENGTH_FILTERS = (includeTermLoan: boolean) => {
  const filters = [
    { value: '-', label: '-' },
    { value: '12 months', label: t`12 months` },
    { value: '15 months', label: t`15 months` },
    { value: '24 months', label: t`24 months` },
    { value: '36 months', label: t`36 months` },
    { value: '48 months', label: t`48 months` },
  ];

  if (includeTermLoan) {
    filters.push({
      value: '60 months',
      label: '60 months',
    });
  }
  return filters;
};

export const APPLICATION_STATUS_FILTERS = () => [
  {
    group: t`Ongoing app`,
    items: [
      { value: APPLICATION_STATUS.open, label: t`Continue app` },
      { value: APPLICATION_STATUS.action_required, label: t`Docs required` },
      { value: APPLICATION_STATUS.manual_review, label: t`Manual check` },
    ],
  },
  {
    group: t`App in review`,
    items: [
      {
        value: APPLICATION_STATUS.awaiting_underwriter,
        label: t`Awaiting underwriter`,
      },
      {
        value: APPLICATION_STATUS.in_underwriting,
        label: t`In review by underwriter`,
      },
    ],
  },
  {
    group: t`App decision`,
    items: [
      { value: APPLICATION_STATUS.offered, label: t`Review offer(s)` },
      {
        value: APPLICATION_STATUS.conditional_offer,
        label: t`Review conditional offer(s)`,
      },
      { value: APPLICATION_STATUS.declined, label: t`App declined` },
      { value: APPLICATION_STATUS.deferred, label: t`App deferred` },
      { value: APPLICATION_STATUS.offer_rejected, label: t`Offer rejected` },
      { value: APPLICATION_STATUS.expired, label: t`Offer expired` },
    ],
  },
  {
    group: t`Funding`,
    items: [
      {
        value: APPLICATION_STATUS.talking_to_customer,
        label: t`Applicant funding steps`,
      },
      { value: APPLICATION_STATUS.funded, label: t`Funded` },
    ],
  },
];

export const ALL_STATUS_VALUES = [...Object.values(APPLICATION_STATUS)];

export const PENDING_STATUS_VALUES = [
  APPLICATION_STATUS.open,
  APPLICATION_STATUS.action_required,
  APPLICATION_STATUS.expired,
];

export const OFFERED_STATUS_VALUES = [
  APPLICATION_STATUS.offered,
  APPLICATION_STATUS.conditional_offer,
];

export const EXPIRING_STATUS_VALUES: (
  | TIntroducerApplication['status']
  | 'isExpiring'
)[] = [
  APPLICATION_STATUS.offered,
  APPLICATION_STATUS.conditional_offer,
  APPLICATION_STATUS.talking_to_customer,
  'isExpiring',
];

export const APPLICATION_STATUS_LABEL = (
  status: keyof typeof APPLICATION_STATUS,
): string => {
  for (const filter of APPLICATION_STATUS_FILTERS()) {
    for (const item of filter.items) {
      if (item.value === status) {
        return item.label;
      }
    }
  }
  return '';
};

const APPLICATION_STATUS_VALUES = () =>
  ({
    declined: t`App declined`,
    deferred: t`App deferred`,
    offered: t`Review offer`,
    conditional_offer: t`Conditional offer`,
    open: t`Continue app`,
    funded: t`Funded`,
    expired: t`Offer expired`,
    talking_to_customer: t`Applicant funding steps`,
    in_underwriting: t`In review by underwriter`,
    manual_review: t`Manual check`,
    awaiting_underwriter: t`Awaiting underwriter`,
    action_required: t`Docs required`,
    offer_revoked: t`Offer revoked`,
    customer_to_link_open_banking: t`Client to link bank account`,
    offer_rejected: t`Offer rejected`,
  }) as const;

type TApplicationStatusValueFields = keyof ReturnType<
  typeof APPLICATION_STATUS_VALUES
>;

export const APPLICATION_STATUS_VALUE = (status: TApplication['status']) => {
  if (APPLICATION_STATUS_VALUES()[status as TApplicationStatusValueFields]) {
    return APPLICATION_STATUS_VALUES()[status as TApplicationStatusValueFields];
  }
  return t`Ongoing`;
};
